import { useT } from "@transifex/react";
import { UserInfo } from "p6m-p6u";
import { useEffect, useState } from "react";
import { useLogger } from "../../hooks/useLogger";
import AccountDropdown from "./AccountDropdown";
import Avatar from "./Avatar";
import { AreaButton, ButtonColorVariant, ButtonVariant } from "../../complex/teacherArea/AreaButton";
import { cp } from "../../config";
import { buildAccountLinks } from "../../utils/userUtils";

interface LoginOrAvatarProps {
    userInfo: UserInfo;
    avatarUrl?: string;
    currentPath: string;
    accountTranslations: {
        myAccount: string;
        myParentAccount: string;
        myLearningAccount: string;
    };
}

// DO NOT DELETE — scroll animation commented out for now, may be re-enabled later
// const _updateAvatarSize = (scrollTop: number) => {
//     const avatar = document.getElementById("header-avatar");
//     if (!avatar) {
//         return;
//     }
//
//     const big = ["max-h-xxl", "min-h-xxl", "max-w-xxl", "min-w-xxl"];
//     const small = ["max-h-xl", "min-h-xl", "max-w-xl", "min-w-xl"];
//
//     if (scrollTop > headerSizeChangeScrollThreshold) {
//         avatar.classList.remove(...big);
//         avatar.classList.add(...small);
//     } else {
//         avatar.classList.remove(...small);
//         avatar.classList.add(...big);
//     }
// };

const _handleClickOutside = (e: MouseEvent, callback: () => void) => {
    if (!(e.target instanceof Element)) {
        return;
    }

    // don't close for marked elements
    if (e.target.closest(".no-close-account-dropdown")) {
        return;
    }

    callback();
};

const _handleEscapeKey = (e: KeyboardEvent, active: boolean, callback: () => void) => {
    if (e.key === "Escape" && active) {
        callback();
    }
};

const LoginOrAvatar = (props: LoginOrAvatarProps) => {
    const { logEvent } = useLogger();
    const t = useT();

    const t_alt_userAvatar = t("User avatar", { _tags: "TeacherArea, Account, ScreenReaderOnly" });
    const t_sr_toggleDropdown = t("toggle navigation menu", {
        _tags: "TeacherArea, Account, ScreenReaderOnly",
    });

    const accountLinks = buildAccountLinks({
        translations: props.accountTranslations,
        userRole: props.userInfo.visitorOrUserRole,
    });

    const t_login = t("Login", { _tags: "TeacherArea, Account" });

    const isUser = !props.userInfo.isAnonymousUser;
    const avatarButtonControlId = "account-dropdown";

    const [showAccountDropdown, setShowAccountDropdown] = useState(false);

    // close popup if click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            _handleClickOutside(e, () => {
                setShowAccountDropdown(false);
            });
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [showAccountDropdown]);

    // close popup on escape key
    useEffect(() => {
        const handleEscapeKey = (e: KeyboardEvent) => {
            _handleEscapeKey(e, showAccountDropdown, () => setShowAccountDropdown(false));
        };

        document.addEventListener("keydown", handleEscapeKey);

        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [showAccountDropdown]);

    // DO NOT DELETE — scroll animation commented out for now, may be re-enabled later
    // useEffect(() => {
    //     return handleHeaderSizeChange({
    //         callback: _updateAvatarSize,
    //         attachToPageLoadEvent: false,
    //     });
    // }, []);

    return (
        <section className="border-stone-400 px-m flex h-full items-center border-l">
            {isUser ? (
                <button
                    className="no-close-account-dropdown cursor-pointer"
                    onClick={() => {
                        setShowAccountDropdown(!showAccountDropdown);
                    }}
                    aria-expanded={showAccountDropdown ? "true" : "false"}
                    aria-controls={avatarButtonControlId}
                    aria-label={t_sr_toggleDropdown}
                    id="burger-avatar-button"
                >
                    <Avatar
                        img={{
                            src: props.avatarUrl ?? "",
                            alt: t_alt_userAvatar,
                        }}
                        borderColor="border-stone-800"
                        sizeClasses="max-h-xxl min-h-xxl max-w-xxl min-w-xxl"
                        id="header-avatar"
                    />
                </button>
            ) : (
                <div className="flex">
                    <AreaButton
                        role="link"
                        variant={ButtonVariant.SECONDARY}
                        colorVariant={ButtonColorVariant.LUMINA}
                        buttonId="header-login-button"
                        href={`${cp.cfg.REACT_APP_P6_BASE_WEBSITE}classic/service/user/login.html`}
                        className="px-m!"
                        onClick={() => {
                            logEvent("Login Button Clicked", { source: "desktop" });
                        }}
                    >
                        {t_login}
                    </AreaButton>
                </div>
            )}
            {showAccountDropdown && (
                <AccountDropdown
                    dropdownLinks={accountLinks}
                    hideDropdown={() => setShowAccountDropdown(false)}
                    currentPath={props.currentPath}
                    id={avatarButtonControlId}
                />
            )}
        </section>
    );
};

export default LoginOrAvatar;
